import { forwardRef, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, ButtonBase, Checkbox, InputBase, Radio, Select, MenuItem } from "@mui/material";
import "./DokuControls.css";

export function DokuActionArea({ interactive = true, className = "", role, tabIndex, onKeyDown, ...props }) {
  if (!interactive) return <div className={className} {...props} />;
  return <ButtonBase component="div" disableRipple className={`doku-mui-area ${className}`} {...props} />;
}

// Nur die Dokumentationsseite verwendet diese Adapter. Fachliche Handler bleiben beim Aufrufer.
export const DokuButton = forwardRef(function DokuButton({ className = "", title, ...props }, ref) {
  return <Button ref={ref} type="button" disableElevation disableRipple
    className={`doku-mui-button ${className}`} title={title} aria-label={props["aria-label"] || title}
    {...props} />;
});

export const DokuInput = forwardRef(function DokuInput({ className = "", style, maxLength, rows, multiline,
  "aria-label": label, placeholder, inputProps, ...props }, ref) {
  return <InputBase inputRef={ref} className={`doku-mui-field ${className}`} style={style}
    multiline={multiline} minRows={multiline ? rows || 3 : undefined} placeholder={placeholder}
    inputProps={{ "aria-label": label || placeholder, maxLength, ...inputProps }} {...props} />;
});

export function DokuCheckbox({ type, ...props }) {
  return <Checkbox disableRipple size="small" className="doku-mui-choice" {...props} />;
}

export function DokuRadio({ type, ...props }) {
  return <Radio disableRipple size="small" className="doku-mui-choice" {...props} />;
}

export function DokuSelect({ className = "", children, ...props }) {
  return <Select size="small" className={`doku-mui-select ${className}`}
    MenuProps={{ PaperProps: { className: "iio doku-mui-menu" } }} {...props}>{children}</Select>;
}
export { MenuItem };

// Bereits bereinigtes Vorschau-HTML bleibt unverändert. Auch dessen eingebettete Aktionen
// werden echte MUI-Buttons; Export und Inhaltsserialisierung verwenden weiterhin ihre eigenen Wege.
function DokuHtmlContent({ html }) {
  const root = useRef(null);
  const [actions, setActions] = useState([]);
  useLayoutEffect(() => {
    root.current.innerHTML = html;
    const next = [...root.current.querySelectorAll("button")].map((button, index) => {
      const host = document.createElement("span");
      host.className = "doku-no-print";
      host.contentEditable = "false";
      const props = {};
      for (const attr of button.attributes) {
        if (attr.name === "class") props.className = attr.value;
        else if (attr.name === "title" || attr.name.startsWith("data-") || attr.name.startsWith("aria-")) props[attr.name] = attr.value;
      }
      const inner = button.innerHTML;
      button.replaceWith(host);
      return { host, props, inner, index };
    });
    setActions(next);
  }, [html]);
  return <><div ref={root} />{actions.map(({ host, props, inner, index }) => createPortal(
    <DokuButton {...props}><span dangerouslySetInnerHTML={{ __html: inner }} /></DokuButton>, host, String(index)
  ))}</>;
}

export function DokuHtml({ html }) {
  return <DokuHtmlContent key={html} html={html} />;
}
